import React from 'react';
import { ILocation } from '../../types/resources/Location';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import LocationUtils from '../../utils/LocationUtils';
import LocationDateTime from './LocationDateTime';
import { useSettingsContext } from '../../context/settings/useSettingsContext';
import sanitizeUtils from '../../utils/SanitizeUtils';

const CardWrapper = styled.div.attrs({
    className: 'cr-shadow cr-text-primary cr-rounded-block cr-w-100 cr-relative'
})`
    align-items: stretch;
    background-color: var(--cr-grey-light-color);
    display: grid;
    grid-template-columns: 1fr 2fr;

    &:not(:last-of-type):after {
        background: var(--cr-alert-color);
        content: '';
        position: absolute;
        left: 50%;
        top: 100%;
        width: 1px;
        height: var(--cr-spacing-xxl);
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

type TImageProps = {
    $imageUrl?: string;
};

const Image = styled.div<TImageProps>`
    background-image: ${(props) => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
    background-position: center;
    background-size: cover;
    border-top-left-radius: var(--cr-block-rounding);
    border-bottom-left-radius: var(--cr-block-rounding);

    @media (max-width: 768px) {
        aspect-ratio: 16/9;
        background-image: ${(props) => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
        border-top-right-radius: var(--cr-block-rounding);
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;

        //mask-image: linear-gradient(black, black), var(--cr-mask-outer);
        //mask-position: top, bottom;
        //mask-repeat: no-repeat;
        //mask-size: 100% calc(100% - 1rem), 100% 1rem;
    }
`;

const Content = styled.div.attrs({
    className: "cr-p-xl cr-flex cr-flex-col cr-gap-sm cr-text-secondary",
})`
    background: linear-gradient(to right,
      #F7F7F77E 0%,
      var(--cr-grey-light-color) 24px
    );
    position: relative;
    z-index: 0;

    &:before {
        background: #F7F7F7;
        content: '';
        height: 100%;
        left: -1px;
        position: absolute;
        top: 0;
        width: 2px;
        z-index: 1;
    }

    @media (max-width: 768px) {
        && {
            padding: var(--cr-spacing-lg);
        }
    }
`;

const ContentBody = styled.div`
    @media (max-width: 768px) {
        && {
            align-items: flex-start;
            flex-direction: column;
        }
    }
`

const Info = styled.div`
`

type TProps = {
    location: ILocation;
};

export default function LocationListItem({ location }: TProps) {
    const { t } = useTranslation();
    const { locationFallbackImage } = useSettingsContext();

    return (
        <CardWrapper className={'cr-location'}>
            <Image className={'cr-image'} $imageUrl={
                location.imageUrl
                || locationFallbackImage
            } />
            <Content className={'cr-text-primary'}>
                <div>
                    <div className={'cr-type'}>{location.typeDescription}</div>
                    <div className="cr-title cr-bold cr-text-alert cr-text-lg cr-pb-xs">
                        {location.name}
                    </div>
                    {location.address &&
                        <div className={'cr-address'}>{location.address}</div>
                    }
                </div>
                <ContentBody className={'cr-flex cr-gap-lg cr-justify-between cr-align-end'}>
                    <Info className={'cr-flex cr-flex-col cr-gap-sm'}>
                        {location.website &&
                            <a href={sanitizeUtils.sanitizeUrl(location.website)} target="_blank" rel="noreferrer"
                               className={'cr-website cr-py'}
                            >
                                {t('view-location')}
                            </a>
                        }
                        <LocationDateTime
                            startDateTime={location.startDateTime}
                            endDateTime={location.endDateTime}
                            dateTimeDisplay={location.dateTimeDisplay}
                            isPrivate={location.isPrivate}
                        />
                    </Info>
                    {location.address &&
                        <a
                            className="cr-route cr-pill cr-text-center"
                            href={LocationUtils.generateRouteUrl(location.address)} target="_blank" rel="noreferrer"
                        >
                            {t('plan-route')}
                        </a>
                    }
                </ContentBody>
            </Content>
        </CardWrapper>
    );
};
